import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import StartDateFilter from 'components/shared/Filters/StartDateFilter';
import EndDateFilter from 'components/shared/Filters/EndDateFilter';
import UserFilter from 'components/shared/Filters/UserFilter';
import GroupFilter from 'components/shared/Filters/GroupFilter';
import TagFilter from 'components/shared/Filters/TagFilter';
import ProjectFilter from 'components/shared/Filters/ProjectFilter';

import { Button } from '@companycam/slab-web';

// #region styled-components
const FilterRowContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--cc_size_spacing_s);
  margin-bottom: var(--cc_size_spacing_xs);
`;
// #endregion

const FilterRow = ({
  clearFilters,
  currentFilters,
  applyFilter,
  hasFilters,
  hideGroups,
  hideProjects,
  hideUsers,
  projectId,
  showTags,
}) => {
  const [clearKey, setClearKey] = useState(0);

  const handleClearAllFilters = () => {
    setClearKey(new Date());
    clearFilters();
  };

  const exposedTagsVisible = hideProjects ? showTags : false;

  return (
    <FilterRowContainer>
      <StartDateFilter
        key={`start-date-${clearKey}`}
        capturedAfter={currentFilters.capturedAfter}
        capturedBefore={currentFilters.capturedBefore}
        onChange={applyFilter}
        dataTestIds={{
          filterButton: 'asset-feed__start-date-filter',
          datePicker: 'asset-feed__captured-after-input',
        }}
      />
      <EndDateFilter
        key={`end-date-${clearKey}`}
        capturedBefore={currentFilters.capturedBefore}
        capturedAfter={currentFilters.capturedAfter}
        onChange={applyFilter}
        dataTestIds={{
          filterButton: 'asset-feed__end-date-filter',
          datePicker: 'asset-feed__captured-before-input',
        }}
      />
      {!hideProjects && (
        <ProjectFilter
          key={`projects-${clearKey}`}
          activeFilterProjects={currentFilters.projects}
          onChange={applyFilter}
          dataTestIds={{
            clearButton: 'asset-feed__clear-project-filter',
            filterButton: 'asset-feed__project-filter',
            searchInput: 'asset-feed__project-filter-search',
          }}
        />
      )}
      {!hideUsers && (
        <UserFilter
          key={`users-${clearKey}`}
          projectId={`${projectId}`}
          activeFilterUsers={currentFilters.users}
          onChange={applyFilter}
          onlyProjectUsers={hideProjects}
          dataTestIds={{
            clearButton: 'asset-feed__clear-users-filter',
            filterButton: 'asset-feed__users-filter',
            searchInput: 'asset-feed__users-filter-search',
          }}
        />
      )}
      {!hideGroups && (
        <GroupFilter
          key={`groups-${clearKey}`}
          activeFilterGroups={currentFilters.groups}
          onChange={applyFilter}
          onlyProjectGroups={hideProjects}
          projectId={`${projectId}`}
          dataTestIds={{
            clearButton: 'asset-feed__clear-groups-filter',
            filterButton: 'asset-feed__groups-filter',
            searchInput: 'asset-feed__groups-filter-search',
          }}
        />
      )}
      {!exposedTagsVisible && (
        <TagFilter
          key={`tags-${clearKey}`}
          projectId={projectId}
          activeFilterTags={currentFilters.tags}
          onChange={applyFilter}
          onlyProjectTags={hideProjects}
          dataTestIds={{
            clearButton: 'asset-feed__clear-tags-filter',
            filterButton: 'asset-feed__tags-filter',
            searchInput: 'asset-feed__tags-filter-search',
          }}
        />
      )}

      {hasFilters ? (
        <div className="ClearAllContainer">
          <Button
            design="minimal"
            color="subtle"
            onClick={handleClearAllFilters}
          >
            <FormattedMessage
              id="AssetFeed.FilterRow.clearAll"
              defaultMessage="Clear All"
              description="Clear All Filters button label"
            />
          </Button>
        </div>
      ) : null}
    </FilterRowContainer>
  );
};

FilterRow.propTypes = {
  clearFilters: PropTypes.func,
  currentFilters: PropTypes.object,
  applyFilter: PropTypes.func,
  hasFilters: PropTypes.bool,
  hideGroups: PropTypes.bool,
  hideProjects: PropTypes.bool,
  hideUsers: PropTypes.bool,
  projectId: PropTypes.number,
  showTags: PropTypes.bool,
};

export default FilterRow;
